import { IHighlightDisplayConfig } from '@trackman/baseball-shared-components/build/services/DataGrid/types/IHighlightDisplayConfig';
import { ISectionHighlight } from '@trackman/baseball-shared-components/build/services/DataGrid/types/ISectionHighlight';
import { v1 as uuid } from 'uuid';
import { emptyBoxScore } from 'constants/Constants';
import { IBoxScoreResponse, IBoxScoreTeam } from 'interfaces/boxScore/IBoxScoreResponse';
import { ColumnName } from '../enums/ColumnName';
import { PlaysGridRowManager } from './PlaysGridRowManager';
import { IBoxScorePitcher } from '../interfaces/boxScore/IBoxScorePitcher';
import { IBoxScoreBatter } from '../interfaces/boxScore/IBoxScoreBatter';
import { emptyGuid } from '../constants/Constants';
import { IEditRowPlayer } from '../interfaces/sessionVerification/plays/IEditRowPlayer';

export interface IBoxScoreDiscrepancy {
  // We treat SecondSource as baseline
  playerId: string;
  isPlayerMissing: boolean; // Player is in SecondSource, but not in TM BoxScore
  isExtraPlayer: boolean; // Player is in TM, but not SS
  discrepantProperties: string[]; // Names of properties that are different
}
export class BoxScoreManager {
  static BoxScoreHighlightGroupName = 'boxScore';

  constructor(rowManager: PlaysGridRowManager) {
    this.rowManager = rowManager;
  }

  rowManager: PlaysGridRowManager;

  trackmanBoxScore: IBoxScoreResponse = { ...emptyBoxScore };

  secondSourceBoxScore: IBoxScoreResponse = { ...emptyBoxScore };

  homePitcherDiscrepancies: IBoxScoreDiscrepancy[] = [];

  awayPitcherDiscrepancies: IBoxScoreDiscrepancy[] = [];

  homeBatterDiscrepancies: IBoxScoreDiscrepancy[] = [];

  awayBatterDiscrepancies: IBoxScoreDiscrepancy[] = [];

  selectedPitcherIds: string[] = [];

  selectedBatterIds: string[] = [];

  highlightNavigationButtonRenderer?: (config: IHighlightDisplayConfig) => React.ReactElement;

  updateBoxScore(isTrackmanBoxScore: boolean, data: IBoxScoreResponse) {
    if (isTrackmanBoxScore) {
      this.trackmanBoxScore = data;
    } else {
      this.secondSourceBoxScore = data;
    }
    this.sortData();
    this.recalculateDiscrepancies();
  }

  invalidateTmBoxScoreCallback?: () => void;

  invalidateTmBoxScore() {
    if (this.invalidateTmBoxScoreCallback) {
      this.invalidateTmBoxScoreCallback();
    }
  }

  selectPitcher(id: string) {
    this.selectedPitcherIds.push(id);
    this.recalculateHighlights(id);
  }

  selectBatter(id: string) {
    this.selectedBatterIds.push(id);
    this.recalculateHighlights(id);
  }

  deselectPitcher(id: string) {
    this.selectedPitcherIds.splice(this.selectedPitcherIds.indexOf(id), 1);
    this.recalculateHighlights();
  }

  deselectBatter(id: string) {
    this.selectedBatterIds.splice(this.selectedBatterIds.indexOf(id), 1);
    this.recalculateHighlights();
  }

  clearSelectedPlayers() {
    this.selectedBatterIds = [];
    this.selectedPitcherIds = [];
    this.recalculateHighlights();
  }

  sortPlayers(tmPlayers: (IBoxScorePitcher | IBoxScoreBatter)[], ssPlayers: (IBoxScorePitcher | IBoxScoreBatter)[]) {
    if (tmPlayers.length === 0 || ssPlayers.length === 0) {
      return [tmPlayers, ssPlayers];
    }
    const tmPlayerIds = tmPlayers.sort((a, b) => a.name.localeCompare(b.name)).map((p) => p.playerId);
    const ssPlayerIds = ssPlayers.sort((a, b) => a.name.localeCompare(b.name)).map((p) => p.playerId);
    const commonPlayerIds = tmPlayerIds.filter((p) => ssPlayerIds.includes(p));
    const sortedPlayerIds = commonPlayerIds
      .concat(tmPlayerIds.filter((p) => !commonPlayerIds.includes(p)))
      .concat(ssPlayerIds.filter((p) => !commonPlayerIds.includes(p)));
    const sortedTmPlayers = tmPlayers.sort((a, b) => sortedPlayerIds.indexOf(a.playerId) - sortedPlayerIds.indexOf(b.playerId));
    const sortedSsPlayers = ssPlayers.sort((a, b) => sortedPlayerIds.indexOf(a.playerId) - sortedPlayerIds.indexOf(b.playerId));
    return [sortedTmPlayers, sortedSsPlayers];
  }

  private sortData() {
    this.sortTeamData(this.trackmanBoxScore.awayTeam, this.secondSourceBoxScore.awayTeam);
    this.sortTeamData(this.trackmanBoxScore.homeTeam, this.secondSourceBoxScore.homeTeam);
  }

  private sortTeamData(tm: IBoxScoreTeam, ss: IBoxScoreTeam) {
    const [tmPitching, ssPitching] = this.sortPlayers(tm.pitching, ss.pitching);
    const [tmBatting, ssBatting] = this.sortPlayers(tm.batting, ss.batting);
    tm.pitching = tmPitching;
    tm.batting = tmBatting;
    ss.pitching = ssPitching;
    ss.batting = ssBatting;
  }

  private recalculateDiscrepancies() {
    this.homePitcherDiscrepancies = this.getDiscrepancies(
      this.trackmanBoxScore.homeTeam.pitching,
      this.secondSourceBoxScore.homeTeam.pitching
    );
    this.awayPitcherDiscrepancies = this.getDiscrepancies(
      this.trackmanBoxScore.awayTeam.pitching,
      this.secondSourceBoxScore.awayTeam.pitching
    );
    this.homeBatterDiscrepancies = this.getDiscrepancies(
      this.trackmanBoxScore.homeTeam.batting,
      this.secondSourceBoxScore.homeTeam.batting
    );
    this.awayBatterDiscrepancies = this.getDiscrepancies(
      this.trackmanBoxScore.awayTeam.batting,
      this.secondSourceBoxScore.awayTeam.batting
    );
  }

  private getDiscrepancies(tm: (IBoxScorePitcher | IBoxScoreBatter)[], ss: (IBoxScorePitcher | IBoxScoreBatter)[]) {
    const result: IBoxScoreDiscrepancy[] = [];
    ss.forEach((player) => {
      const tmPlayer = tm.find((p) => p.playerId === player.playerId);
      if (!tmPlayer) {
        result.push({
          playerId: player.playerId,
          isPlayerMissing: true,
          isExtraPlayer: false,
          discrepantProperties: [],
        } as IBoxScoreDiscrepancy);
      } else {
        const diff = this.comparePlayers(player, tmPlayer);
        if (diff.length > 0) {
          result.push({ playerId: player.playerId, isPlayerMissing: false, isExtraPlayer: false, discrepantProperties: diff });
        }
      }
    });
    const extraPlayers = tm.filter((p) => !result.some((v) => p.playerId === v.playerId));
    extraPlayers.forEach((player) => {
      result.push({ playerId: player.playerId, isExtraPlayer: true, isPlayerMissing: false, discrepantProperties: [] });
    });
    return result;
  }

  private comparePlayers(a: IBoxScorePitcher | IBoxScoreBatter, b: IBoxScorePitcher | IBoxScoreBatter): string[] {
    const diff: string[] = [];
    const normalizeData = (input: any) => {
      return !input || input === '0' ? '' : input;
    };
    for (const key in a) {
      if (typeof a[key] === 'function') {
        continue;
      }

      if (normalizeData(b[key]) !== normalizeData(a[key])) {
        diff.push(key);
      }
    }
    for (const k in b) {
      if (typeof b[k] === 'function' || diff.includes(k)) {
        continue;
      }
      if (normalizeData(a[k]) !== normalizeData(b[k])) {
        diff.push(k);
      }
    }

    return diff;
  }

  private getId(player: IEditRowPlayer) {
    return player.id !== emptyGuid ? player.id : player.name;
  }

  recalculateHighlights(scrollToPlayerId?: string) {
    const highlights = [];
    this.rowManager.highlightManager.removeGroup(BoxScoreManager.BoxScoreHighlightGroupName);
    if (this.selectedBatterIds.length === 0 && this.selectedPitcherIds.length === 0) {
      this.rowManager.updateGrid();
      return;
    }
    let scrollToRow;
    let currentPitcherHighlight: ISectionHighlight | undefined;
    let currentBatterHighlight: ISectionHighlight | undefined;
    const { rows } = this.rowManager;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.isVisible()) {
        // highlight doesn't work for hidden elements
        continue;
      }
      if (this.selectedPitcherIds.includes(this.getId(row.pitcher))) {
        if (currentPitcherHighlight) {
          currentPitcherHighlight.toRowIndex = i;
        } else {
          if (scrollToRow === undefined && scrollToPlayerId && this.getId(row.pitcher) === scrollToPlayerId) {
            scrollToRow = i;
          }
          currentPitcherHighlight = {
            id: uuid(),
            groupId: BoxScoreManager.BoxScoreHighlightGroupName,
            fromRowIndex: i,
            toRowIndex: i,
            columnIndex: this.rowManager.columns.findIndex((c) => c.columnName === ColumnName.Pitcher),
            className: 'player-highlight',
            customElementRender: this.highlightNavigationButtonRenderer,
          };
        }
      } else if (currentPitcherHighlight) {
        highlights.push(currentPitcherHighlight);
        currentPitcherHighlight = undefined;
      }

      if (this.selectedBatterIds.includes(this.getId(row.batter))) {
        if (currentBatterHighlight) {
          currentBatterHighlight.toRowIndex = i;
        } else {
          if (scrollToRow === undefined && scrollToPlayerId && this.getId(row.batter) === scrollToPlayerId) {
            scrollToRow = i;
          }
          currentBatterHighlight = {
            id: uuid(),
            groupId: BoxScoreManager.BoxScoreHighlightGroupName,
            fromRowIndex: i,
            toRowIndex: i,
            columnIndex: this.rowManager.columns.findIndex((c) => c.columnName === ColumnName.Batter),
            className: 'player-highlight',
            customElementRender: this.highlightNavigationButtonRenderer,
          };
        }
      } else if (currentBatterHighlight) {
        highlights.push(currentBatterHighlight);
        currentBatterHighlight = undefined;
      }
    }
    if (currentPitcherHighlight) {
      highlights.push(currentPitcherHighlight);
    }
    if (currentBatterHighlight) {
      highlights.push(currentBatterHighlight);
    }
    this.rowManager.highlightManager.setHighlights(highlights);
    if (scrollToRow !== undefined) {
      this.rowManager.scrollToRow(scrollToRow);
    }
    this.rowManager.updateGrid();
  }
}
